import { accountErasure } from "@app/shared";
import { useState } from "react";
import { appConfig } from "../../../../app.config";
import { Button } from "../components/ui/button";
import { READING_WIDTH } from "../lib/layout";

/**
 * « Mes données » : les deux droits RGPD qui se codent, cote visiteur.
 *
 * Sans l'option `auth`, l'app n'a pas de compte : la page explique alors comment
 * exercer ses droits par courrier electronique, et n'affiche aucun bouton qui
 * repondrait 401. Le ticket 4 activera les boutons en meme temps que les comptes.
 *
 * L'export est un simple lien : le backend repond avec un en-tete
 * `content-disposition: attachment`, le navigateur enregistre le fichier. Pas de
 * blob a fabriquer, pas d'objet URL a liberer, rien a rater.
 *
 * La phrase sous les boutons dit ce qu'efface la suppression. C'est celle du template, sauf si
 * l'app a ecrit la sienne (`legal.account.erasure`, lue par `accountErasure` de `@app/shared`,
 * qui garde la regle du marqueur d'un texte vide) : une suppression ne se rattrape pas, et une
 * app dont l'effacement a d'autres effets doit le dire avant le clic.
 */

export function MyDataPage() {
  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");

  const erase = async (): Promise<void> => {
    setState("busy");
    try {
      const response = await fetch("/api/me", { method: "DELETE" });
      setState(response.ok ? "done" : "error");
    } catch {
      setState("error");
    }
    setConfirming(false);
  };

  return (
    <section className={`flex flex-col gap-6 ${READING_WIDTH}`}>
      <div>
        <h1 className="text-2xl font-semibold">Mes données</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Vous pouvez obtenir une copie des données que {appConfig.name} détient sur vous, et
          demander leur suppression. Le détail des traitements est décrit dans la politique de
          confidentialité.
        </p>
      </div>

      {appConfig.features.auth ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <a
              className="text-sm underline underline-offset-2"
              href="/api/me/export"
              rel="nofollow"
            >
              Exporter mes données
            </a>
            {confirming ? (
              <>
                <Button disabled={state === "busy"} onClick={() => void erase()}>
                  Confirmer la suppression
                </Button>
                <Button onClick={() => setConfirming(false)}>Annuler</Button>
              </>
            ) : (
              <Button onClick={() => setConfirming(true)}>Supprimer mon compte</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{accountErasure(appConfig)}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Ce service ne crée pas de compte utilisateur. Pour exercer vos droits, écrivez à{" "}
          <a className="underline underline-offset-2" href={`mailto:${appConfig.contactEmail}`}>
            {appConfig.contactEmail}
          </a>
          .
        </p>
      )}

      {state === "done" ? <p className="text-sm">Vos données ont été supprimées.</p> : null}
      {state === "error" ? (
        <p className="text-sm text-destructive">
          L'opération n'a pas abouti. Réessayez plus tard ou écrivez à {appConfig.contactEmail}.
        </p>
      ) : null}
    </section>
  );
}
